<template>
  <v-dialog v-model="dialog" width="680" @click:outside="cancel" :persistent="isLoading">
    <v-card class="pa-4">
      <v-card-title>
        <h3><strong>{{ title }}</strong></h3>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-form ref="form">
            <v-row>
              <v-col cols="12">
                <v-select density="compact" hide-details="auto" v-model="siteCode" :items="dialogData.siteCodeList"
                  item-value="code" item-title="code_name" :label="$t('sitezoneRegisterDialog.label.siteCode')"
                  :disabled="isUpdate" :rules="[rules.required($t('sitezoneRegisterDialog.label.siteCode'))]"
                  return-object></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field density="compact" hide-details="auto" v-model="zoneCode" maxlength="10"
                  :label="$t('sitezoneRegisterDialog.label.zoneCode')" :disabled="isUpdate"
                  :rules="zoneCodeRules"></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field density="compact" hide-details="auto" v-model="zoneName" maxlength="100"
                  :label="$t('sitezoneRegisterDialog.label.zoneName')"
                  :rules="[rules.required($t('sitezoneRegisterDialog.label.zoneName')), rules.maxLength(100)]"></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
        <span class="ml-4" v-if="errorMessage" style="color: red">{{ errorMessage }}</span>
        <div class="ml-4" v-for="data in errorList" :key="data.message" style="color: red">{{ data.message }}</div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text @click="cancel" :disabled="isLoading">{{ $t('common.action.cancel') }}</v-btn>
        <v-btn color="indigo darken-1" dark @click="ok" :loading="isLoading">
          {{ okLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/apis/staff";
  
export const ZONE_DIALOG_MODE = Object.freeze({
  // 新規登録
  NEW: 'new',
  // 更新
  UPDATE: 'upd',
});

export default {
  props: {
    dialogData: {
      type: Object,
      required: true,
    },
    handleOk: {
      type: Function,
      required: true,
    },
    handleCancel: {
      type: Function,
      required: true,
    },
  },
  computed: {
    // 更新モードかどうか
    isUpdate() {
      return this.dialogData.mode == ZONE_DIALOG_MODE.UPDATE;
    },
    title() {
      const zone = this.$t('sitezoneRegisterDialog.label.zone');
      const mode = this.isUpdate
        ? this.$t('sitezoneRegisterDialog.label.titleUpdate')
        : this.$t('sitezoneRegisterDialog.label.titleRegister');
      return zone + mode;
    },
    okLabel() {
      return this.isUpdate
        ? this.$t('sitezoneRegisterDialog.label.save')
        : this.$t('sitezoneRegisterDialog.label.register');
    },
    // 更新モードはゾーンコードを変更できないため、入力チェックは登録モードのみ行う
    zoneCodeRules() {
      if (this.isUpdate) return [];
      return [
        this.rules.required(this.$t('sitezoneRegisterDialog.label.zoneCode')),
        this.rules.maxLength(10),
        this.rules.zoneCodeFormat,
      ];
    },
  },
  data() {
    return {
      rules: {
        required: fieldName => {
          return v => {
            return this.hasLength(v) || this.$t('sitezoneRegisterDialog.validation.required', [fieldName]);
          }
        },
        maxLength: len => {
          return v => {
            if (!this.hasLength(v)) return true;
            if (v.length <= len) return true;
            return len + this.$t('sitezoneRegisterDialog.validation.max');
          }
        },
        zoneCodeFormat: v => {
          return /^[0-9A-Za-z-]*$/.test(v) || this.$t('sitezoneRegisterDialog.validation.zoneCodeFormat');
        },
      },
      dialog: true,
      isLoading: false,

      // 入力
      siteCode: this.dialogData.siteCode ?? null,
      zoneCode: "",
      zoneName: "",

      // エラー出力
      errorMessage: "",
      errorList: [],
    };
  },
  mounted() {
    if (!this.isUpdate) return;
    const zone = this.dialogData.zone;
    this.siteCode = this.dialogData.siteCodeList.find(e => e.code == zone.siteCode) ?? null;
    this.zoneCode = zone.zoneCode;
    this.zoneName = zone.zoneName;
  },
  methods: {
    hasLength(v) {
      return !(v == null || v == undefined || v == "");
    },
    cancel() {
      if (this.isLoading) return;
      this.handleCancel();
    },
    async ok() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) return;

      this.isLoading = true;
      this.errorMessage = "";
      this.errorList = [];

      const param = {
        siteCode: this.siteCode?.code,
        zoneCode: this.zoneCode,
        zoneName: this.zoneName,
        // 親ゾーン（BIN集計対象）はこの画面では扱わないため固定でfalseを送る
        binFlag: false,
      };

      api.post(this.isUpdate ? "/zone/update" : "/zone/register", param)
        .then((response) => {
          if (response.data?.status == 0) {
            this.handleOk();
          } else {
            this.errorMessage = response.data?.errorMessage;
            this.errorList = response.data?.errorList || [];
          }
        })
        .catch(() => {
          this.errorMessage = this.$t('common.message.unknownError');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
